import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotifications } from './useNotifications';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';
import { USER_TYPE_GUEST } from '../constants';

function timeAgo(timestamp) {
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // If older than 30 days, show absolute date
  if (days > 30) {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  return "just now";
}


function NotificationsModal(props) {
    const [open, setOpen] = useState(false);

    const [requestingNotifications, setRequestingNotifications] = useState(false); 
    
    const enableNotifications = () => {
        props.requestNotifications();
        setRequestingNotifications(true);
    };

    useEffect(() => {
        if (props.notificationsEnabled === true)
        {
            setRequestingNotifications(false);
        }
        if (props.notificationsFailed === true)
        {
            setRequestingNotifications(false);
        }
    }, [props.notificationsEnabled, props.notificationsFailed]);

    const { notifications, markNotificationAsRead, deleteNotification } = useNotifications(30000);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const handleOpen = () => setOpen(true);
    const handleClose = (event, reason) => {
        if (reason === "backdropClick") return; // ignore backdrop clicks
        setOpen(false);
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        if (selectedId !== null) {
        deleteNotification(selectedId);
        }
        setConfirmOpen(false);
        setSelectedId(null);
    };

    const handleCancelDelete = () => {
        setConfirmOpen(false);
        setSelectedId(null);
    };

    const unreadCount = notifications.filter((n) => !n.read).length;

    return (
        <React.Fragment>
        <IconButton
            onClick={handleOpen}
            size="large"
            edge="start"
            color="inherit"
            aria-label="notifications"
            sx={{ mr: 0 }}
        >
            <Badge
            badgeContent={unreadCount}
            sx={{
                "& .MuiBadge-badge": {
                backgroundColor: "#f26419",
                color: "white",
                },
            }}
            >
            <NotificationsIcon />
            </Badge>
        </IconButton>

        <Dialog
            open={open}
            onClose={handleClose}
            sx={{
            "& .MuiDialog-container": {
                "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "500px",
                },
            },
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2 }}>
            Notifications
            <IconButton
                onClick={handleClose}
                aria-label="close"
                sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            </DialogTitle>

            <DialogContent sx={{ m: 0, p: 1 }}>
                {props.notificationsEnabled ? null : (
                    <Typography 
                        variant="body1" 
                        style={{ textAlign: 'center' }} 
                        component="div"
                    >
                    {props.userType == USER_TYPE_GUEST ? 
                        "Notifications are not available for guest accounts" 
                    :
                        <Button onClick={enableNotifications} size="medium">
                            {requestingNotifications ? (
                            <CircularProgress size="1rem" />
                            ) : (
                            <NotificationsIcon />
                            )} 
                            Enable Notifications
                        </Button>
                    }
                    
                </Typography>
                )}

                {props.notificationsFailed && (
                    <Box sx={{ m: 0, p: 1 }} display="flex" flexDirection="column" gap={1}>
                        <Typography 
                        variant="body2" 
                        style={{ textAlign: 'left' }} 
                        component="div"
                        >
                        To receive notifications, install Qonsulta on your Home Screen first then tap the Enable Notifications button.
                        </Typography>
                    </Box>
                )}
                
                {props.notificationsEnabled && notifications.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#777' }}>
                No notifications yet
                </p>
                ) : (
                <List>
                {notifications.map((n, index) => (
                    <React.Fragment key={n.id}>
                    <ListItem
                        alignItems="flex-start"
                        sx={{ opacity: n.read ? 0.6 : 1 }}
                        secondaryAction={
                            <Stack direction="column" spacing={0} alignItems="center">
                            {!n.read && (
                                <IconButton
                                edge="end"
                                aria-label="mark as read"
                                onClick={() => markNotificationAsRead(n.id)}
                                >
                                <MarkEmailReadIcon />
                                </IconButton>
                            )}
                            <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => handleDeleteClick(n.id)}
                            >
                                <DeleteIcon />
                            </IconButton>
                            </Stack>
                        }
                        >
                        <ListItemText
                            primary={n.title}
                            secondary={
                            <>
                                {n.body}
                                <br />
                                <small>{timeAgo(n.timestamp)}</small>
                            </>
                            }
                        />
                        </ListItem>

                    {index < notifications.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
                </List>
            )}
            </DialogContent>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onClose={handleCancelDelete}>
            <DialogTitle>Delete Notification</DialogTitle>
            <DialogContent>
            Are you sure you want to delete this notification?
            </DialogContent>
            <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel</Button>
            <Button onClick={handleConfirmDelete}>
                Delete
            </Button>
            </DialogActions>
        </Dialog>
        </React.Fragment>
    );
}

export default NotificationsModal;
